import { useState } from 'react';

interface DirectMessagesProps {
  onClose: () => void;
}

interface Chat {
  id: string;
  userId: string;
  userName: string;
  userAvatar: string;
  userLevel: number;
  lastMessage: string;
  timestamp: Date;
  unread: number;
  online: boolean;
}

interface Message {
  id: string;
  senderId: string;
  text: string;
  timestamp: Date;
  type: 'text' | 'achievement';
  achievement?: {
    type: string;
    title: string;
    icon: string;
  };
}

export function DirectMessages({ onClose }: DirectMessagesProps) {
  const [selectedChat, setSelectedChat] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');

  const chats: Chat[] = [
    {
      id: '1',
      userId: '2',
      userName: 'Ana Silva',
      userAvatar: 'https://readdy.ai/api/search-image?query=professional%20businesswoman%20avatar%20portrait%20simple%20background%20corporate%20attire%20confident%20smile&width=100&height=100&seq=dm-ana-001&orientation=squarish',
      userLevel: 15,
      lastMessage: 'Parabéns pela conquista! 🎉',
      timestamp: new Date(Date.now() - 5 * 60 * 1000),
      unread: 2,
      online: true
    },
    {
      id: '2',
      userId: '3',
      userName: 'Carlos Mendes',
      userAvatar: 'https://readdy.ai/api/search-image?query=professional%20businessman%20avatar%20portrait%20simple%20background%20business%20suit%20friendly%20expression&width=100&height=100&seq=dm-carlos-002&orientation=squarish',
      userLevel: 18,
      lastMessage: 'Vamos fazer aquele desafio juntos?',
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      unread: 1,
      online: true
    },
    {
      id: '3',
      userId: '4',
      userName: 'Beatriz Costa',
      userAvatar: 'https://readdy.ai/api/search-image?query=professional%20businesswoman%20avatar%20portrait%20simple%20background%20elegant%20attire%20warm%20smile&width=100&height=100&seq=dm-beatriz-003&orientation=squarish',
      userLevel: 14,
      lastMessage: 'Obrigada pela dica!',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      unread: 0,
      online: false
    }
  ];

  const messages: Message[] = [
    {
      id: '1',
      senderId: '2',
      text: 'Oi! Vi que você alcançou o nível 12!',
      timestamp: new Date(Date.now() - 10 * 60 * 1000),
      type: 'text'
    },
    {
      id: '2',
      senderId: 'current',
      text: 'Obrigado! Foi muito difícil 😅',
      timestamp: new Date(Date.now() - 8 * 60 * 1000),
      type: 'text'
    },
    {
      id: '3',
      senderId: '2',
      text: 'Parabéns pela conquista! 🎉',
      timestamp: new Date(Date.now() - 5 * 60 * 1000),
      type: 'text'
    }
  ];

  const selectedChatData = chats.find(c => c.id === selectedChat);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[600px] flex overflow-hidden">
        {/* Chat List */}
        <div className="w-80 border-r border-slate-200 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-xl font-black text-slate-800">Mensagens</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center hover:bg-slate-200 transition-colors"
            >
              <i className="ri-close-line text-lg text-slate-600"></i>
            </button>
          </div>

          {/* Search */}
          <div className="p-3 border-b border-slate-200">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
              <input
                type="text"
                placeholder="Buscar conversas..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Chat List */}
          <div className="flex-1 overflow-y-auto">
            {chats.map((chat) => (
              <button
                key={chat.id}
                onClick={() => setSelectedChat(chat.id)}
                className={`w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors ${
                  selectedChat === chat.id ? 'bg-teal-50' : ''
                }`}
              >
                <div className="relative flex-shrink-0">
                  <img
                    src={chat.userAvatar}
                    alt={chat.userName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  {chat.online && (
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                  )}
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                    {chat.userLevel}
                  </div>
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-bold text-slate-800 text-sm truncate">{chat.userName}</p>
                    <span className="text-xs text-slate-400">
                      {Math.floor((Date.now() - chat.timestamp.getTime()) / (1000 * 60))}min
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 truncate">{chat.lastMessage}</p>
                </div>
                {chat.unread > 0 && (
                  <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                    {chat.unread}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Chat Window */}
        <div className="flex-1 flex flex-col">
          {selectedChat ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <img
                      src={selectedChatData?.userAvatar}
                      alt={selectedChatData?.userName}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    {selectedChatData?.online && (
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                    )}
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">{selectedChatData?.userName}</p>
                    <p className="text-xs text-slate-500">
                      {selectedChatData?.online ? 'Online' : 'Offline'}
                    </p>
                  </div>
                </div>
                <button className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center hover:bg-slate-200 transition-colors">
                  <i className="ri-more-2-fill text-slate-600"></i>
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.senderId === 'current' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                        message.senderId === 'current'
                          ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white'
                          : 'bg-white text-slate-800 border border-slate-200'
                      }`}
                    >
                      <p className="text-sm">{message.text}</p>
                      <p className={`text-xs mt-1 ${
                        message.senderId === 'current' ? 'text-white/70' : 'text-slate-400'
                      }`}>
                        {message.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Input */}
              <div className="p-4 border-t border-slate-200 bg-white">
                <div className="flex items-center gap-2">
                  <button className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-slate-200 transition-colors">
                    <i className="ri-emotion-line text-xl text-slate-600"></i>
                  </button>
                  <button className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-slate-200 transition-colors">
                    <i className="ri-trophy-line text-xl text-slate-600"></i>
                  </button>
                  <input
                    type="text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Digite uma mensagem..."
                    className="flex-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <button className="w-10 h-10 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-full flex items-center justify-center hover:scale-105 transition-transform shadow-lg">
                    <i className="ri-send-plane-fill text-white"></i>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center">
                  <i className="ri-message-3-line text-5xl text-purple-500"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Suas Mensagens</h3>
                <p className="text-slate-500">Selecione uma conversa para começar</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
